import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { lang } from '../config/lang';
import { motion } from 'framer-motion';
import DevisGenerator from '../components/DevisGenerator';
import CategoriesService from '../services/categoriesService';
import DynamicFieldsForm from '../components/DynamicFieldsForm';
import SafeImage from '../components/SafeImage';
import PageContentService from '../services/pageContentService';
import { useInlineEdit } from '../components/admin/InlineEditContext';
import InlineEditableText from '../components/admin/InlineEditableText';
import InlineEditableImage from '../components/admin/InlineEditableImage';

const Services = () => {
  const location = useLocation();
  const inlineCtx = useInlineEdit && useInlineEdit();
  const [page, setPage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [descriptionMode, setDescriptionMode] = useState('categories');
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [showGlobalComment, setShowGlobalComment] = useState(false);
  const [globalComment, setGlobalComment] = useState('');
  const [locationState, setLocationState] = useState('');
  const [budget, setBudget] = useState('');
  const [timing, setTiming] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 48.8566, lng: 2.3522 });
  const [dynamicAddress, setDynamicAddress] = useState('');
  const [addressForm, setAddressForm] = useState({
    type: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'France',
    floor: '',
    isApartment: false
  });
  
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });

  const [subcategorySurfaces, setSubcategorySurfaces] = useState({});
  const [manualSurface, setManualSurface] = useState('');
  const [categorySurfaces, setCategorySurfaces] = useState({});
  const [isMultiCategory, setIsMultiCategory] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  // États pour la zone de texte globale d'informations complémentaires
  const [showGlobalAdditionalInfo, setShowGlobalAdditionalInfo] = useState(false);
  const [globalAdditionalInfo, setGlobalAdditionalInfo] = useState('');

  // **NOUVEAUX ÉTATS POUR L'API**
  const [apiCategories, setApiCategories] = useState({
    interior: [],
    exterior: [],
    professional: []
  });
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [dbTypeImages, setDbTypeImages] = useState({ complete: '', multiSector: '' });

  // **CHARGER LES CATÉGORIES DEPUIS L'API**
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoadingCategories(true);
        setCategoriesError(null);
        
        // Charger les catégories organisées pour l'affichage
        const categoriesForDisplay = await CategoriesService.getCategoriesForDisplay();

        setApiCategories({
          interior: categoriesForDisplay.interior,
          exterior: categoriesForDisplay.exterior,
          professional: categoriesForDisplay.professional
        });
        
        console.log('✅ Catégories chargées depuis l\'API:', {
          interior: categoriesForDisplay.interior.length,
          exterior: categoriesForDisplay.exterior.length,
          professional: categoriesForDisplay.professional.length
        });
        
      } catch (error) {
        console.error('🚨 Erreur chargement catégories:', error);
        setCategoriesError(error.message);
        
        // En cas d'erreur, utiliser les données de fallback
        console.log('🔄 Utilisation des données de fallback...');
        
      } finally {
        setIsLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const p = await PageContentService.getPage('services');
        setPage(p);
      } catch (_) {
        setPage(null);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchTypeImages = async () => {
      try {
        const all = await CategoriesService.getAllCategories();
        const complete = all.find(c => c.nom === 'Rénovation Complète');
        const partielle = all.find(c => c.nom === 'Rénovation Partielle');
        setDbTypeImages({
          complete: complete?.image || '',
          multiSector: partielle?.image || ''
        });
      } catch (_e) {
        setDbTypeImages({ complete: '', multiSector: '' });
      }
    };
    fetchTypeImages();
  }, []);

  // **HELPER - Obtenir les catégories (API ou fallback)**
  const getCategories = () => {
    if (isLoadingCategories) {
      return { interior: [], exterior: [], professional: [] };
    }
    return apiCategories;
  };

  // Gérer la présélection depuis l'IA
  useEffect(() => {
    if (location.state?.preselection && location.state?.fromAI) {
      const { renovationType, categories, shouldAutoSelect, surfaceData, targetCategory } = location.state.preselection;
      
      console.log('Présélection reçue:', { renovationType, categories, targetCategory, surfaceData });
      
      if (shouldAutoSelect) {
        // Cas 1: Navigation directe vers une catégorie spécifique (ex: SOL)
        if (targetCategory && renovationType === 'specific' && categories.length === 1) {
          const categoryData = categories[0];
          const foundCategory = findCategoryByTitle(categoryData.title);
          
          console.log('Navigation directe vers:', categoryData.title, foundCategory);
          
          if (foundCategory) {
            setSelectedCategory(foundCategory);
            setDescriptionMode('categories');
            
            // Présélectionner les sous-catégories collectées par l'IA
            const selectedSubs = categoryData.selectedSubcategories || categoryData.subcategories;
            setSelectedSubcategories(selectedSubs);
            
            console.log('Sous-catégories présélectionnées:', selectedSubs);
            
            // Intégrer les surfaces collectées par l'IA
            if (surfaceData && surfaceData[categoryData.title]) {
              console.log('Application des surfaces:', surfaceData[categoryData.title]);
              
              selectedSubs.forEach(subcategory => {
                if (surfaceData[categoryData.title][subcategory]) {
                  setSubcategorySurfaces(prev => ({
                    ...prev,
                    [subcategory]: {
                      surface: surfaceData[categoryData.title][subcategory]
                    }
                  }));
                }
              });
            }
          }
        }
        // Cas 2: Partielle (anciennement multisectorielle) - sélectionner "Rénovation partielle"
        else if (categories.length > 1 || ['multisectoral','multisectorielle','partial','partielle'].includes(renovationType)) {
          const foundPartielle = findCategoryByTitle('Rénovation Partielle') || lang.fr.services.types.multiSector;
          setSelectedCategory(foundPartielle);
          setDescriptionMode('categories');
          
          // Présélectionner toutes les catégories et sous-catégories
          const allSubcategories = [];
          categories.forEach(cat => {
            const selectedSubs = cat.selectedSubcategories || cat.subcategories;
            if (selectedSubs) {
              allSubcategories.push(...selectedSubs);
              // Élargir la catégorie correspondante pour qu'elle soit visible
              setExpandedCategory(cat.title);
              
              // Intégrer les surfaces collectées par l'IA
              if (surfaceData && surfaceData[cat.title]) {
                selectedSubs.forEach(subcategory => {
                  if (surfaceData[cat.title][subcategory]) {
                    setSubcategorySurfaces(prev => ({
                      ...prev,
                      [subcategory]: {
                        surface: surfaceData[cat.title][subcategory]
                      }
                    }));
                  }
                });
              }
            }
          });
          setSelectedSubcategories(allSubcategories);
        }
        // Cas 3: Rénovation complète
        else if (renovationType === 'complete') {
          const completeCategory = findCategoryByTitle('Rénovation Complète');
          setSelectedCategory(completeCategory || null);
          setDescriptionMode('freeText');
        }
      }
    }
  }, [location.state, isLoadingCategories]);

  const findCategoryByTitle = (title) => {
    console.log('Recherche de catégorie pour:', title);
    const categories = getCategories();
    
    // Chercher dans les catégories intérieures par nom français
    const interiorCategory = categories.interior.find(cat => cat.nom === title);
    if (interiorCategory) {
      return {
        title: interiorCategory.nom,
        image: interiorCategory.image,
        subcategories: interiorCategory.sous_categories.map(sub => sub.nom)
      };
    }
    
    // Chercher dans les catégories extérieures par nom français
    const exteriorCategory = categories.exterior.find(cat => cat.nom === title);
    if (exteriorCategory) {
      return {
        title: exteriorCategory.nom,
        image: exteriorCategory.image,
        subcategories: exteriorCategory.sous_categories.map(sub => sub.nom)
      };
    }
    
    // Chercher dans les catégories professionnelles par nom français
    const professionalCategory = categories.professional.find(cat => cat.nom === title);
    if (professionalCategory) {
      return {
        title: professionalCategory.nom,
        image: professionalCategory.image,
        subcategories: professionalCategory.sous_categories.map(sub => sub.nom)
      };
    }
    
    console.log('Catégorie non trouvée:', title);
    return null;
  };

  const handleCategoryClick = (category) => {
    // Si on clique sur un type, mapper vers la catégorie BD correspondante
    if (category.title === "Rénovation partielle") {
      const found = findCategoryByTitle('Rénovation Partielle');
      setSelectedCategory(found || category);
    } else if (category.title === "Rénovation complète") {
      const found = findCategoryByTitle('Rénovation Complète');
      setSelectedCategory(found || category);
    } else {
      setSelectedCategory(category);
    }
    setIsFormOpen(true);
    
    // Réinitialiser les états
    setSelectedSubcategories([]);
    setSubcategorySurfaces({});
    setCategorySurfaces({});
    setCustomDescription('');
    
    // Cas 1: Rénovation partielle
    if (category.title === "Rénovation partielle") {
      setDescriptionMode('categories');
      setIsMultiCategory(true);
    }
    // Cas 2: Catégorie spécifique (ex: Cuisine, Salle de bain, etc.)
    else if (category.subcategories) {
      setDescriptionMode('categories');
      setIsMultiCategory(false);
    }
    // Cas 3: Rénovation complète → FORCER freeText pour matching automatique
    else if (category.title === "Rénovation complète") {
      setDescriptionMode('freeText'); // ⭐ FORCER freeText
      setIsMultiCategory(false);
    }
  };

  const handleSubcategoryChange = (subcategory, category, event) => {
    event.stopPropagation();
    setSelectedSubcategories(prev => {
      if (prev.includes(subcategory)) {
        return prev.filter(sub => sub !== subcategory);
      }
      return [...prev, subcategory];
    });
  };

  const handleSurfaceChange = (subcategory, field, value) => {
    setSubcategorySurfaces(prev => ({
      ...prev,
      [subcategory]: {
        ...prev[subcategory],
        [field]: value
      }
    }));
  };

  const calculateTotalSurface = () => {
    let total = 0;
    selectedSubcategories.forEach(subcategory => {
      const subcatData = subcategorySurfaces[subcategory];
      if (subcatData?.surface_piece) {
        total += parseFloat(subcatData.surface_piece);
      } else if (subcatData?.surface_elements) {
        total += parseFloat(subcatData.surface_elements);
      } else if (subcatData?.surface) {
        // Fallback pour l'ancien format
        total += parseFloat(subcatData.surface);
      }
    });
    
    if (total > 0) {
      return Math.ceil(total / 5) * 5;
    }
    return 0;
  };

  const toggleCategory = (category) => {
    if (expandedCategory === category) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(category);
    }
  };

  const isFormValid = () => {
    const isComplete = selectedCategory?.title?.toLowerCase() === "rénovation complète";
    const isPart1Valid = selectedCategory && (
      isComplete ? 
      customDescription.trim() !== '' : 
      (descriptionMode === 'categories' ? selectedSubcategories.length > 0 : customDescription.trim() !== '')
    );

    const isPart2Valid = budget && timing && locationState && addressForm.type;

    const isPart3Valid = personalInfo.firstName.trim() !== '' && 
                        personalInfo.lastName.trim() !== '' && 
                        personalInfo.phone.trim() !== '' && 
                        personalInfo.email.trim() !== '';

    return isPart1Valid && isPart2Valid && isPart3Valid;
  };

  const handleAddressFormChange = (field, value) => {
    setAddressForm(prev => ({
      ...prev,
      [field]: value,
      isApartment: field === 'type' ? value === 'apartment' : prev.isApartment
    }));
    
    if ((field === 'postalCode' || field === 'city') && locationState.trim()) {
      setTimeout(() => {
        searchAddress(buildSearchQuery());
      }, 500);
    }
  };

  const buildSearchQuery = () => {
    let searchQuery = '';
    
    if (addressForm.street) {
      searchQuery += addressForm.street;
    }
    
    if (addressForm.postalCode) {
      searchQuery += ` ${addressForm.postalCode}`;
    }
    
    if (addressForm.city) {
      searchQuery += ` ${addressForm.city}`;
    }
    
    if (addressForm.country) {
      searchQuery += ` ${addressForm.country}`;
    }
    
    return searchQuery.trim();
  };

  const searchAddress = async (query) => {
    if (query.length > 3) {
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(
            query
          )}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&components=country:fr&language=fr`
        );
        const data = await response.json();
        if (data.predictions) {
          setAddressSuggestions(data.predictions);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Erreur lors de la recherche d\'adresse:', error);
      }
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearchClick = async () => {
    const searchQuery = buildSearchQuery();
    if (searchQuery) {
      await searchAddress(searchQuery);
      setDynamicAddress(searchQuery);
      setLocation(searchQuery);
    }
  };

  const handleAddressSelect = (suggestion) => {
    const fullDescription = suggestion.description;
    
    const postalCodeMatch = fullDescription.match(/\b(\d{5})\b/);
    const postalCode = postalCodeMatch ? postalCodeMatch[1] : '';
    
    let city = '';
    if (postalCode) {
      const parts = fullDescription.split(postalCode);
      if (parts.length > 1) {
        city = parts[1].replace(/^[,\s]+/, '').split(',')[0].trim();
      }
    }
    
    const addressParts = fullDescription.split(',');
    const street = addressParts[0] || '';
    
    if (!city && addressParts.length >= 2) {
      city = addressParts[1].trim();
    }
    
    const country = 'France';

    setAddressForm(prev => ({
      ...prev,
      street,
      city,
      postalCode,
      country
    }));

    const formattedAddress = `${street}, ${city}, ${postalCode}, ${country}`;
    setLocation(formattedAddress);
    setDynamicAddress(formattedAddress);
    setShowSuggestions(false);
  };

  const handleRemoveLocation = () => {
    setLocation('');
    setDynamicAddress('');
    setAddressForm({
      type: '',
      street: '',
      postalCode: '',
      city: '',
      country: 'France',
      floor: '',
      isApartment: false
    });
  };

  const handleCategorySurfaceChange = (categoryTitle, value) => {
    setCategorySurfaces(prev => ({
      ...prev,
      [categoryTitle]: value
    }));
  };

  // Si une catégorie est sélectionnée
  if (selectedCategory) {
    return (
      <div className="min-h-screen bg-gray-900 py-6 md:py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl md:text-4xl font-bold text-center mb-6 md:mb-12 text-white">
            {selectedCategory.title}
          </h1>

          <div className="max-w-[90vw] md:max-w-6xl mx-auto">
            <button
              onClick={() => setSelectedCategory(null)}
              className="flex items-center space-x-2 text-white mb-6 hover:text-primary transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Retour</span>
            </button>

            <div className="relative mb-6 md:mb-8">
              <SafeImage
                src={selectedCategory.image}
                alt={selectedCategory.title}
                className="w-full h-[40vh] md:h-[50vh] object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
            </div>

            <div className="bg-gray-800 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
              <p className="text-gray-300 text-base md:text-lg">
                {selectedCategory.description || `Services professionnels de ${selectedCategory.title.toLowerCase()}`}
              </p>
            </div>

            <div className="space-y-6 md:space-y-8">
              {/* Section 1: Besoins */}
              <div className="bg-gray-800 rounded-lg p-4 md:p-6">
                <div className="flex items-center mb-4 md:mb-6">
                  <div className="bg-primary text-white rounded-full w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-lg md:text-xl font-bold mr-3 md:mr-4">
                    1
                  </div>
                  <h2 className="text-xl md:text-2xl font-semibold text-white">Sélectionnez vos besoins</h2>
                </div>

                <div className="space-y-4">
                  {(selectedCategory.title?.toLowerCase() === "rénovation complète") ? (
                    <div className="space-y-6">
                      <textarea
                        value={customDescription}
                        onChange={(e) => setCustomDescription(e.target.value)}
                        placeholder="Décrivez votre projet avec vos propres mots..."
                        className="w-full bg-gray-700 text-white rounded-lg p-3 md:p-4 h-32 md:h-48 focus:ring-2 focus:ring-primary text-sm md:text-base"
                      />
                    </div>
                  ) : (
                    <div>
                      <div className="flex space-x-4 mb-4 md:mb-6">
                        <button
                          onClick={() => setDescriptionMode('categories')}
                          className={`flex-1 py-2 md:py-3 px-3 md:px-4 rounded-lg text-sm md:text-base ${
                            descriptionMode === 'categories'
                              ? 'bg-primary text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          {(selectedCategory.title?.toLowerCase() === "rénovation partielle") ? "Par catégorie" : "Par sous-catégorie"}
                        </button>
                        <button
                          onClick={() => setDescriptionMode('freeText')}
                          className={`flex-1 py-2 md:py-3 px-3 md:px-4 rounded-lg text-sm md:text-base ${
                            descriptionMode === 'freeText'
                              ? 'bg-primary text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          Avec vos propres mots
                        </button>
                      </div>

                      {descriptionMode === 'freeText' ? (
                        <textarea
                          value={customDescription}
                          onChange={(e) => setCustomDescription(e.target.value)}
                          placeholder="Décrivez votre projet avec vos propres mots..."
                          className="w-full bg-gray-700 text-white rounded-lg p-3 md:p-4 h-32 md:h-48 focus:ring-2 focus:ring-primary text-sm md:text-base"
                        />
                      ) : (
                        (selectedCategory.title?.toLowerCase() === "rénovation partielle") ? (
                          <div className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                              {Object.entries(lang.fr.services.interior.categories).map(([key, category]) => (
                                <div key={key} className="space-y-2">
                                  <motion.div
                                    whileHover={{ scale: 1.01 }}
                                    className={`bg-gray-700 rounded-lg cursor-pointer transition-all h-[12vh] md:h-[15vh] flex items-center justify-center ${
                                      selectedSubcategories.some(sub => category.subcategories.includes(sub)) ? 'ring-2 ring-primary' : ''
                                    }`}
                                    onClick={() => toggleCategory(category.title)}
                                  >
                                    <div className="text-center p-3">
                                      <h3 className="text-white text-sm md:text-base font-semibold text-center">
                                        {category.title}
                                      </h3>
                                      <div className="mt-2">
                                        <svg
                                          className={`w-4 h-4 text-white transform transition-transform mx-auto ${
                                            expandedCategory === category.title ? 'rotate-180' : ''
                                          }`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                  </motion.div>

                                  {expandedCategory === category.title && (
                                    <motion.div
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      exit={{ opacity: 0, height: 0 }}
                                      transition={{ duration: 0.2 }}
                                      className="bg-gray-700 rounded-lg p-3 md:p-4 mx-2"
                                    >
                                      <div className="space-y-4">
                                        {/* Sous-catégories avec champs dynamiques */}
                                        {category.sous_categories?.map((subcategory, subIndex) => (
                                          <div key={subIndex} className="border-l-2 border-gray-600 pl-6 py-2">
                                            <label className="flex items-start space-x-3 cursor-pointer group hover:bg-gray-600/20 rounded p-2 transition-colors">
                                              <input
                                                type="checkbox"
                                                checked={selectedSubcategories.includes(subcategory.nom)}
                                                onChange={(e) => handleSubcategoryChange(subcategory.nom, category, e)}
                                                className="mt-1 w-4 h-4 text-primary bg-gray-600 border-gray-500 rounded focus:ring-primary focus:ring-2"
                                              />
                                              <span className="text-gray-300 text-sm group-hover:text-white flex-1">{subcategory.nom}</span>
                                            </label>
                                            
                                            {/* Nouveau composant pour les champs dynamiques */}
                                            <DynamicFieldsForm
                                              subcategory={subcategory.nom}
                                              categoryName={category.nom}
                                              values={subcategorySurfaces[subcategory.nom] || {}}
                                              onChange={handleSurfaceChange}
                                              isVisible={selectedSubcategories.includes(subcategory.nom)}
                                            />
                                          </div>
                                        ))}
                                      </div>
                                    </motion.div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {selectedCategory.subcategories?.map((subcategory, index) => (
                              <div key={index} className="border-l-2 border-gray-600 pl-6 py-2">
                                <label className="flex items-start space-x-3 cursor-pointer group hover:bg-gray-600/20 rounded p-2 transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={selectedSubcategories.includes(subcategory)}
                                    onChange={(e) => handleSubcategoryChange(subcategory, selectedCategory, e)}
                                    className="mt-1 w-4 h-4 text-primary bg-gray-600 border-gray-500 rounded focus:ring-primary focus:ring-2"
                                  />
                                  <span className="text-gray-300 text-sm group-hover:text-white flex-1">{subcategory}</span>
                                </label>
                                
                                {/* Nouveau composant pour les champs dynamiques */}
                                <DynamicFieldsForm
                                  subcategory={subcategory}
                                  categoryName={selectedCategory.title}
                                  values={subcategorySurfaces[subcategory] || {}}
                                  onChange={handleSurfaceChange}
                                  isVisible={selectedSubcategories.includes(subcategory)}
                                />
                              </div>
                            ))}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
                
                {/* Zone de texte globale pour informations complémentaires */}
                <div className="border-t border-gray-600 pt-4 mt-6">
                  <label className="flex items-center cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={showGlobalAdditionalInfo}
                      onChange={(e) => setShowGlobalAdditionalInfo(e.target.checked)}
                      className="w-4 h-4 text-primary bg-gray-600 border-gray-500 rounded focus:ring-primary focus:ring-2 mr-3"
                    />
                    <span className="text-gray-300 text-sm">
                      Ajouter des informations complémentaires sur votre projet
                    </span>
                  </label>
                  
                  {showGlobalAdditionalInfo && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <textarea
                        placeholder="Décrivez les spécificités de votre projet : état actuel, contraintes particulières, préférences de matériaux, délais spéciaux..."
                        value={globalAdditionalInfo}
                        onChange={(e) => setGlobalAdditionalInfo(e.target.value)}
                        rows={4}
                        className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary placeholder-gray-400 resize-none"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Ces informations nous aideront à personnaliser votre devis et à mieux comprendre vos besoins
                      </p>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Section 2: Budget/Timing/Surface */}
              <div className="bg-gray-800 rounded-lg p-4 md:p-6">
                <div className="flex items-center mb-4 md:mb-6">
                  <div className="bg-primary text-white rounded-full w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-lg md:text-xl font-bold mr-3 md:mr-4">
                    2
                  </div>
                  <h2 className="text-xl md:text-2xl font-semibold text-white">Dites-en-nous plus</h2>
                </div>

            <div className={`grid grid-cols-1 ${(selectedCategory.title?.toLowerCase() === "rénovation partielle") ? "md:grid-cols-3" : "md:grid-cols-3"} gap-4 md:gap-8 mb-6`}>
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-white">Budget estimé</h3>
                    <select
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Sélectionner un budget</option>
                      <option value="0-5000">0 - 5 000 €</option>
                      <option value="5000-10000">5 000 - 10 000 €</option>
                      <option value="10000-20000">10 000 - 20 000 €</option>
                      <option value="20000-50000">20 000 - 50 000 €</option>
                      <option value="50000+">50 000 € et plus</option>
                    </select>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-white">Timing</h3>
                    <select
                      value={timing}
                      onChange={(e) => setTiming(e.target.value)}
                      className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Sélectionner un timing</option>
                      <option value="immediate">Immédiatement</option>
                      <option value="1month">Dans 1 mois</option>
                      <option value="3months">Dans 3 mois</option>
                      <option value="6months">Dans 6 mois</option>
                      <option value="more">Plus tard</option>
                    </select>
                  </div>

            {(selectedCategory.title?.toLowerCase() === "rénovation partielle") ? (
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-white">Surface du bien (m²)</h3>
                      <input
                        type="number"
                        min="1"
                        placeholder="Surface totale du bien"
                        value={manualSurface}
                        onChange={(e) => setManualSurface(e.target.value)}
                        className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                      />
                    </div>
            ) : (selectedCategory.title?.toLowerCase() === "rénovation complète") ? (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold mb-3 text-white">Surface du bien (m²)</h3>
                        <input
                          type="number"
                          min="1"
                          placeholder="Surface totale du bien"
                          value={manualSurface}
                          onChange={(e) => setManualSurface(e.target.value)}
                          className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <h3 className="text-lg font-semibold mb-3 text-white">Surface travaux (m²)</h3>
                        <input
                          type="number"
                          min="1"
                          placeholder="Surface des travaux"
                          value={manualSurface}
                          onChange={(e) => setManualSurface(e.target.value)}
                          className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </>
                  ) : (
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-white">Surface du bien (m²)</h3>
                      {descriptionMode === 'categories' && selectedSubcategories.length > 0 ? (
                        <div className="w-full bg-gray-600 text-white rounded-lg p-3 flex items-center">
                          Environ {calculateTotalSurface()} m²
                        </div>
                      ) : (
                        <input
                          type="number"
                          min="1"
                          placeholder="Surface en m²"
                          value={manualSurface}
                          onChange={(e) => setManualSurface(e.target.value)}
                          className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 text-white">Localisation</h3>
                  <div className="space-y-4">
                    <select
                      value={addressForm.type}
                      onChange={(e) => handleAddressFormChange('type', e.target.value)}
                      className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Type de local</option>
                      <option value="office">Bureau</option>
                      <option value="commercial">Local commercial</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="warehouse">Entrepôt</option>
                      <option value="other">Autre</option>
                    </select>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        value={addressForm.street}
                        onChange={(e) => handleAddressFormChange('street', e.target.value)}
                        placeholder="Rue, numéro"
                        className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                      />
                      <input
                        type="text"
                        value={addressForm.city}
                        onChange={(e) => handleAddressFormChange('city', e.target.value)}
                        placeholder="Ville"
                        className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        value={addressForm.postalCode}
                        onChange={(e) => handleAddressFormChange('postalCode', e.target.value)}
                        placeholder="Code postal"
                        className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                      />
                      <input
                        type="text"
                        value={addressForm.country}
                        onChange={(e) => handleAddressFormChange('country', e.target.value)}
                        placeholder="Pays"
                        className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                      />
                    </div>

                    {(addressForm.type === 'office' || addressForm.type === 'commercial') && (
                      <input
                        type="text"
                        value={addressForm.floor}
                        onChange={(e) => handleAddressFormChange('floor', e.target.value)}
                        placeholder="Étage / Numéro"
                        className="w-full bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                      />
                    )}

                    <button
                      type="button"
                      onClick={handleSearchClick}
                      className="w-full bg-primary text-white py-3 rounded-lg hover:bg-primary/90 transition-colors font-semibold"
                    >
                      Rechercher l'adresse
                    </button>

                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div className="bg-gray-700 rounded-lg max-h-48 overflow-y-auto">
                        {addressSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            onClick={() => handleAddressSelect(suggestion)}
                            className="p-3 hover:bg-gray-600 cursor-pointer text-white border-b border-gray-600 last:border-b-0"
                          >
                            {suggestion.description}
                          </div>
                        ))}
                      </div>
                    )}

                    {dynamicAddress && (
                      <div className="mt-4">
                        <h4 className="text-md font-medium mb-2 text-white">Localisation confirmée</h4>
                        <div className="relative h-[300px] rounded-lg overflow-hidden border border-gray-600">
                          <iframe
                            src={`https://www.google.com/maps?q=${encodeURIComponent(dynamicAddress)}&output=embed`}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveLocation}
                          className="mt-2 text-red-400 hover:text-red-300 text-sm"
                        >
                          Modifier l'adresse
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 3: Coordonnées */}
              <div className="bg-gray-800 rounded-lg p-4 md:p-6">
                <div className="flex items-center mb-4 md:mb-6">
                  <div className="bg-primary text-white rounded-full w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-lg md:text-xl font-bold mr-3 md:mr-4">
                    3
                  </div>
                  <h2 className="text-xl md:text-2xl font-semibold text-white">Vos coordonnées</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={personalInfo.firstName}
                    onChange={(e) => setPersonalInfo({...personalInfo, firstName: e.target.value})}
                    placeholder="Prénom *"
                    className="bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                  />
                  <input
                    type="text"
                    value={personalInfo.lastName}
                    onChange={(e) => setPersonalInfo({...personalInfo, lastName: e.target.value})}
                    placeholder="Nom *"
                    className="bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                  />
                  <input
                    type="tel"
                    value={personalInfo.phone}
                    onChange={(e) => setPersonalInfo({...personalInfo, phone: e.target.value})}
                    placeholder="Téléphone *"
                    className="bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                  />
                  <input
                    type="email"
                    value={personalInfo.email}
                    onChange={(e) => setPersonalInfo({...personalInfo, email: e.target.value})}
                    placeholder="Email *"
                    className="bg-gray-700 text-white rounded-lg p-3 focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <button
                className={`w-full py-3 md:py-4 rounded-lg transition-colors font-semibold text-base md:text-lg ${
                  isFormValid()
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                }`}
                disabled={!isFormValid()}
              >
                Demander un devis
              </button>

              {/* Aperçu du devis structuré */}
              {isFormValid() && (
                <DevisGenerator
                  formData={{
                    selectedCategory,
                    budget,
                    timing,
                    locationState,
                    addressForm,
                    personalInfo
                  }}
                  selectedSubcategories={selectedSubcategories}
                  subcategorySurfaces={subcategorySurfaces}
                  customDescription={customDescription}
                  descriptionMode={descriptionMode}
                  projectType="services"
                />
              )}
            </div>
          </div>
        </div>

        
      </div>
    );
  }

  // Page principale des services
  return (
    <div className="min-h-screen bg-gray-900 py-6 md:py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-2xl md:text-4xl font-bold text-center mb-6 md:mb-12 text-white">
          {(() => {
            const sections = Array.isArray(page?.sections) ? page.sections : [];
            const sec = sections.find(s => (s.key === 'services_header' || s.type === 'services_header')) || null;
            const t = sec?.data?.title?.fr || sec?.title || '';
            return inlineCtx ? (
              <InlineEditableText
                value={inlineCtx.getValue('services_header', 'title.fr', t)}
                sectionKey="services_header"
                fieldPath="title.fr"
                placeholder={''}
              />
            ) : (
              t || null
            );
          })()}
        </h1>

        {/* Indicateur de chargement */}
        {isLoadingCategories && (
          <div className="text-center text-white mb-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-2">Chargement des catégories...</p>
          </div>
        )}

        {/* Erreur de chargement */}
        {categoriesError && (
          <div className="bg-red-600/20 border border-red-600 rounded-lg p-4 mb-8 text-center text-white">
            <p className="font-semibold">⚠️ Impossible de charger les catégories depuis l'API</p>
            <p className="text-sm mt-1">Utilisation des données locales de secours</p>
          </div>
        )}

        <div className="max-w-[90vw] md:max-w-6xl mx-auto space-y-12">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold mb-6 text-white text-center">
              {(() => {
                const sections = Array.isArray(page?.sections) ? page.sections : [];
                const sec = sections.find(s => (s.key === 'types' || s.type === 'types')) || null;
                const t = sec?.data?.title?.fr || sec?.title || '';
                return inlineCtx ? (
                  <InlineEditableText
                    value={inlineCtx.getValue('types', 'title.fr', t)}
                    sectionKey="types"
                    fieldPath="title.fr"
                    placeholder={''}
                  />
                ) : (
                  t || null
                );
              })()}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(lang.fr.services.types)
                .filter(([key]) => !['multisectoral'].includes(key))
                .map(([key, service]) => {
                const imageForTypeTile = (typeKey) => {
                  if (typeKey === 'complete') {
                    if (dbTypeImages.complete) return CategoriesService.resolveImageUrl(dbTypeImages.complete);
                    const cat = findCategoryByTitle('Rénovation Complète');
                    if (cat?.image) return CategoriesService.resolveImageUrl(cat.image);
                    return '';
                  }
                  if (typeKey === 'multiSector') {
                    if (dbTypeImages.multiSector) return CategoriesService.resolveImageUrl(dbTypeImages.multiSector);
                    const cat = findCategoryByTitle('Rénovation Partielle');
                    if (cat?.image) return CategoriesService.resolveImageUrl(cat.image);
                    return '';
                  }
                  return '';
                };
                if (key === 'title') return null;
                return (
                  <motion.div
                    key={key}
                    whileHover={{ scale: 1.02 }}
                    className="bg-gray-800 rounded-lg overflow-hidden cursor-pointer group"
                    onClick={() => handleCategoryClick(service)}
                  >
                    <div className="relative h-[30vh] md:h-[40vh]">
                      <SafeImage
                        src={imageForTypeTile(key)}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/25 group-hover:bg-black/60 transition-colors flex items-center justify-center">
                        <h3 className="text-white text-xl md:text-2xl font-semibold text-center px-4">
                          {service.title}
                        </h3>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Rénovation Intérieure */}
          <div>
            <h2 className="text-xl md:text-2xl font-semibold mb-6 text-white text-center">
              {(() => {
                const sections = Array.isArray(page?.sections) ? page.sections : [];
                const sec = sections.find(s => (s.key === 'interior' || s.type === 'interior')) || null;
                const t = sec?.data?.title?.fr || sec?.title || '';
                return inlineCtx ? (
                  <InlineEditableText
                    value={inlineCtx.getValue('interior', 'title.fr', t)}
                    sectionKey="interior"
                    fieldPath="title.fr"
                    placeholder={''}
                  />
                ) : (
                  t || null
                );
              })()}
            </h2>
            {!isLoadingCategories && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {getCategories().interior.map((category) => (
                  <motion.div
                    key={category.nom}
                    whileHover={{ scale: 1.02 }}
                    className="bg-gray-800 rounded-lg overflow-hidden cursor-pointer group"
                    onClick={() => handleCategoryClick({
                      title: category.nom,
                      image: category.image,
                      subcategories: category.sous_categories?.map(sub => sub.nom) || category.subcategories
                    })}
                  >
                    <div className="relative h-[25vh] md:h-[30vh]">
                      <SafeImage
                        src={category.image}
                        alt={category.nom}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/25 group-hover:bg-black/60 transition-colors flex items-center justify-center">
                        <h3 className="text-white text-lg md:text-xl font-semibold text-center px-4">
                          {category.nom}
                        </h3>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Rénovation Extérieure */}
          <div>
            <h2 className="text-xl md:text-2xl font-semibold mb-6 text-white text-center">
              {(() => {
                const sections = Array.isArray(page?.sections) ? page.sections : [];
                const sec = sections.find(s => (s.key === 'exterior' || s.type === 'exterior')) || null;
                const t = sec?.data?.title?.fr || sec?.title || '';
                return inlineCtx ? (
                  <InlineEditableText
                    value={inlineCtx.getValue('exterior', 'title.fr', t)}
                    sectionKey="exterior"
                    fieldPath="title.fr"
                    placeholder={''}
                  />
                ) : (
                  t || null
                );
              })()}
            </h2>
            {!isLoadingCategories && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {getCategories().exterior.map((category) => (
                  <motion.div
                    key={category.nom}
                    whileHover={{ scale: 1.02 }}
                    className="bg-gray-800 rounded-lg overflow-hidden cursor-pointer group"
                    onClick={() => handleCategoryClick({
                      title: category.nom,
                      image: category.image,
                      subcategories: category.sous_categories?.map(sub => sub.nom) || category.subcategories
                    })}
                  >
                    <div className="relative h-[25vh] md:h-[30vh]">
                      <SafeImage
                        src={category.image}
                        alt={category.nom}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/25 group-hover:bg-black/60 transition-colors flex items-center justify-center">
                        <h3 className="text-white text-lg md:text-xl font-semibold text-center px-4">
                          {category.nom}
                        </h3>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Section professionnelle retirée pour éviter la duplication */}
        </div>
      </div>

      
    </div>
  );
};

export default Services;
